import * as React from 'react';
import { t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();

const normalizeItem = (i) => ({
  id: safeString(i?.id).trim(),
  text: safeString(i?.text).trim(),
  category: safeString(i?.category).trim() || 'direct',
  severity: safeString(i?.severity).trim() || 'info',
  state: safeString(i?.state).trim() || 'unconfirmed',
  // The effect's causing diff path (empty for risk items): what lets the host
  // navigate a flagged item back to the product editor screen that produced it.
  path: safeString(i?.path).trim(),
  // The oracle's concrete before/after illustration (pricing/breaking effects).
  example: safeString(i?.example).trim(),
});

/**
 * Parses a review-checklist JSON string into a normalized groups array
 * ([{ id, title, summary, items: [{ id, text, category, severity, state }] }]), or
 * null when the payload is not a checklist. The checklist is grouped by LOGICAL
 * CHANGE (the related edits that serve one editing goal); a LEGACY flat payload
 * ({ items: [...] }, the pre-grouping schema) is wrapped into a single group. The
 * server is the authoritative normalizer — this only guards against a malformed
 * round-trip so the panel never renders broken rows.
 */
export const parseChecklistGroups = (json) => {
  try {
    const root = JSON.parse(safeString(json) || 'null');
    if (!root) return null;
    if (Array.isArray(root.groups)) {
      return root.groups
        .map((g) => ({
          id: safeString(g?.id).trim(),
          title: safeString(g?.title).trim(),
          summary: safeString(g?.summary).trim(),
          items: (Array.isArray(g?.items) ? g.items : []).map(normalizeItem).filter((i) => i.id && i.text),
        }))
        .filter((g) => g.id);
    }
    if (Array.isArray(root.items)) {
      const items = root.items.map(normalizeItem).filter((i) => i.id && i.text);
      return items.length > 0
        ? [{ id: 'review', title: t('Review checklist'), summary: '', items }]
        : [];
    }
    return null;
  } catch {
    return null;
  }
};

/** Serializes checklist groups back to the round-trip JSON contract. */
export const serializeChecklistGroups = (groups) => JSON.stringify({
  groups: (Array.isArray(groups) ? groups : []).map((g) => ({
    id: g.id,
    title: g.title,
    summary: g.summary,
    items: (Array.isArray(g.items) ? g.items : []).map((i) => ({
      id: i.id, text: i.text, category: i.category, severity: i.severity, state: i.state, path: i.path, example: i.example,
    })),
  })),
});

/** All items across every group, in render order. */
export const flattenChecklistItems = (groups) => (Array.isArray(groups) ? groups : [])
  .flatMap((g) => (Array.isArray(g.items) ? g.items : []));

const SEVERITY_STYLES = {
  breaking: { background: '#f8d7da', color: '#842029', label: 'Breaking' },
  warning: { background: '#fff3cd', color: '#664d03', label: 'Warning' },
  info: { background: '#e9ecef', color: '#495057', label: 'Info' },
};

const CATEGORY_LABELS = {
  direct: 'Direct',
  pricing: 'Pricing',
  flowOn: 'Flow-on',
  risk: 'Verify',
};

const badgeStyle = (background, color) => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  background,
  color,
  whiteSpace: 'nowrap',
});

/**
 * Renders an example's value markers as styled spans: the oracle wraps the BEFORE
 * value in ~~double tildes~~ and the AFTER value in **double asterisks** (a prompt
 * rule), so "was ~~45~~ and is now **67**" shows the old value struck through and
 * the new one highlighted. Plain regex-split into React spans — no HTML injection
 * surface — and text without markers (older checklists) passes through unchanged.
 */
const renderExampleText = (text) => {
  const source = `${text ?? ''}`;
  const parts = source.split(/(\*\*[^*]+\*\*|~~[^~]+~~)/g);
  if (parts.length === 1) return source;
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong
          key={idx}
          style={{ fontStyle: 'normal', color: '#0f5132', background: '#d1e7dd', borderRadius: 3, padding: '0 4px' }}
        >
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('~~') && part.endsWith('~~') && part.length > 4) {
      return (
        <span
          key={idx}
          style={{ fontStyle: 'normal', color: '#b02a37', textDecoration: 'line-through', background: '#f8d7da', borderRadius: 3, padding: '0 4px' }}
        >
          {part.slice(2, -2)}
        </span>
      );
    }
    return part;
  });
};

const stateButtonStyle = (selected, tone) => ({
  border: selected ? `1px solid ${tone}` : '1px solid #ced4da',
  background: selected ? tone : '#fff',
  color: selected ? '#fff' : '#495057',
  borderRadius: 4,
  padding: '3px 10px',
  fontSize: 12,
  fontWeight: selected ? 700 : 500,
  cursor: 'pointer',
});

const ChecklistItemRow = ({ item, onItemStateChange, disabled }) => {
  const severity = SEVERITY_STYLES[item.severity] ?? SEVERITY_STYLES.info;
  const rowTone = item.state === 'unintended' ? '#fff5f5' : item.state === 'intended' ? '#f6fff8' : '#fff';
  return (
    <div
      data-tagname={`checklistItem_${item.id}`}
      style={{
        border: '1px solid #dee2e6',
        borderLeft: `4px solid ${item.state === 'unintended' ? '#dc3545' : item.state === 'intended' ? '#28a745' : severity.background}`,
        borderRadius: 6,
        background: rowTone,
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={badgeStyle(severity.background, severity.color)}>{t(severity.label)}</span>
        <span style={badgeStyle('#e7f1ff', '#0a58ca')}>{t(CATEGORY_LABELS[item.category] ?? CATEGORY_LABELS.direct)}</span>
      </div>
      <div style={{ fontSize: 13, color: '#212529', lineHeight: 1.45 }}>{item.text}</div>
      {item.example && (
        <div
          data-tagname="checklistExample"
          style={{
            // Deliberately quiet: the severity badges own the colour — the example
            // only needs the indented-callout structure to read as an illustration.
            background: '#f8f9fa',
            borderLeft: '3px solid #adb5bd',
            borderRadius: 4,
            padding: '6px 10px',
            fontSize: 12.5,
            color: '#495057',
            lineHeight: 1.5,
          }}
        >
          <span style={{
            fontWeight: 700,
            fontSize: 10.5,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginRight: 8,
            color: '#6c757d',
          }}
          >
            {t('Example')}
          </span>
          <span style={{ fontStyle: 'italic' }}>{renderExampleText(item.example)}</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          data-tagname="checklistIntended"
          disabled={!!disabled}
          style={stateButtonStyle(item.state === 'intended', '#28a745')}
          title={t('This effect is what the change was meant to do. Click again to clear.')}
          onClick={() => onItemStateChange?.(item.id, item.state === 'intended' ? 'unconfirmed' : 'intended')}
        >
          {t('Intended')}
        </button>
        <button
          type="button"
          data-tagname="checklistUnintended"
          disabled={!!disabled}
          style={stateButtonStyle(item.state === 'unintended', '#dc3545')}
          title={t('Flag this effect: it was NOT meant to happen. Flagged items are the assistant\u2019s top investigation priority, and Process Flags takes you to the screens to correct them. Click again to clear.')}
          onClick={() => onItemStateChange?.(item.id, item.state === 'unintended' ? 'unconfirmed' : 'unintended')}
        >
          {t('Flag')}
        </button>
      </div>
    </div>
  );
};

/**
 * The change review's CHECKLIST panel: the session's deliverable, rendered as the main
 * tab beside the chat, grouped by LOGICAL CHANGE — the cluster of related edits that
 * together serve one editing goal (e.g. creating an option and setting it as a pricing
 * option), as classified by the product specification oracle against the actual
 * definition. Each group's header carries its title, consequence summary and a
 * severity rollup; the effects sit beneath as checklist items. The ORACLE owns the
 * groups and item wording (each consult refreshes them — the server merges by item
 * id); the USER owns each item's state, set here: Intended (the change was supposed
 * to do this) or Unintended (it was not — the assistant treats those as its top
 * investigation priority). Clicking the selected state again clears the item back to
 * unconfirmed. States round-trip on every chat request, so the assistant always
 * reasons over the user's latest verdicts.
 *
 * Props:
 * - groups: [{ id, title, summary, items: [{ id, text, category, severity, state }] }]
 *   (empty/null = no checklist yet)
 * - onItemStateChange(id, nextState): user verdict changed
 * - disabled?: while a turn is running
 */
export const ChangeReviewChecklistPanel = ({ groups, onItemStateChange, disabled }) => {
  const groupList = Array.isArray(groups) ? groups : [];
  const allItems = flattenChecklistItems(groupList);

  // Collapse state is keyed by group id, which is stable across re-assessments —
  // a consult that refreshes the checklist keeps the user's collapsed groups.
  const [collapsedGroups, setCollapsedGroups] = React.useState({});
  const toggleGroup = (id) => setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  if (allItems.length === 0 && groupList.length === 0) {
    return (
      <div style={{ padding: 24, color: '#6c757d', fontSize: 13, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{t('No checklist yet')}</div>
        {t('The change is assessed by logical change — the related edits that serve one goal — with each one\u2019s direct, pricing and flow-on effects verified against the product definition. Work through the effects marking each one Intended or Flag; flagged effects become the assistant\u2019s top priority to investigate, and Process Flags takes you to the screens to correct them.')}
      </div>
    );
  }

  const reviewed = allItems.filter((i) => i.state === 'intended' || i.state === 'unintended').length;
  const unintended = allItems.filter((i) => i.state === 'unintended').length;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      padding: '8px 6px',
      // A soft backdrop so the white item cards read as cards instead of the whole
      // panel glaring — lowers overall brightness without touching any colours.
      background: '#f1f3f5',
      borderRadius: 6,
    }}
    >
      {allItems.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 4px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#495057' }}>
            {`${reviewed}/${allItems.length} ${t('reviewed')}`}
          </div>
          <div style={{ flex: 1, height: 6, background: '#e9ecef', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${allItems.length === 0 ? 0 : Math.round((reviewed / allItems.length) * 100)}%`,
              height: '100%',
              background: unintended > 0 ? '#dc3545' : '#28a745',
              transition: 'width 0.2s',
            }}
            />
          </div>
          {unintended > 0 && (
            <span style={badgeStyle('#f8d7da', '#842029')}>
              {`${unintended} ${t('flagged')}`}
            </span>
          )}
        </div>
      )}

      {groupList.map((group) => {
        const breaking = group.items.filter((i) => i.severity === 'breaking').length;
        const warning = group.items.filter((i) => i.severity === 'warning').length;
        const isCollapsed = !!collapsedGroups[group.id];
        return (
          <div
            key={group.id}
            data-tagname={`checklistGroup_${group.id}`}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            <div
              data-tagname="checklistGroupHeader"
              role="button"
              tabIndex={0}
              title={isCollapsed ? t('Expand this logical change') : t('Collapse this logical change')}
              onClick={() => toggleGroup(group.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(group.id); }
              }}
              style={{
                background: '#e9ecef',
                border: '1px solid #dee2e6',
                borderRadius: 6,
                padding: '8px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#6c757d', width: 12, flexShrink: 0 }}>
                  {isCollapsed ? '\u25B8' : '\u25BE'}
                </span>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', flex: 1, minWidth: 160 }}>
                  {group.title || group.id}
                </div>
                {breaking > 0 && (
                  <span style={badgeStyle(SEVERITY_STYLES.breaking.background, SEVERITY_STYLES.breaking.color)}>
                    {`${breaking} ${t('breaking')}`}
                  </span>
                )}
                {warning > 0 && (
                  <span style={badgeStyle(SEVERITY_STYLES.warning.background, SEVERITY_STYLES.warning.color)}>
                    {`${warning} ${t('warning')}`}
                  </span>
                )}
                {isCollapsed && (
                  <span style={badgeStyle('#e9ecef', '#495057')}>
                    {`${group.items.length} ${t(group.items.length === 1 ? 'effect' : 'effects')}`}
                  </span>
                )}
              </div>
              {!isCollapsed && group.summary && (
                <div style={{ fontSize: 12, color: '#495057', lineHeight: 1.45 }}>{group.summary}</div>
              )}
            </div>
            {!isCollapsed && group.items.map((item) => (
              <ChecklistItemRow
                key={item.id}
                item={item}
                onItemStateChange={onItemStateChange}
                disabled={disabled}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};
